// Certificates (CR_20260921_Asset_Lifecycle T1): what each asset's category requires and what it holds, read from
// public.asset_cert_status (security invoker — the caller sees the certificates of the assets they can see); the
// rules per category; and the manager's writes. Files live in the private asset-docs bucket and open through a
// short-lived signed URL. Writes are managers' only (RLS + storage policy, §4.5) — the UI only mirrors that.
import {
  type CertStatusRow,
  DOC_BUCKET,
  docPath,
  kindSuggestions,
} from '@fleetspoke/core/lifecycle';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { lifecycleKeys } from './lifecycle';

export type { CertStatusRow } from '@fleetspoke/core/lifecycle';

export interface CertRule {
  id: string;
  category_id: string;
  kind: string;
  valid_for_days: number | null;
  blocks_dispatch: boolean;
  remind_days_before: number;
}

export const certKeys = {
  all: ['certs'] as const,
  asset: (assetId: string) => ['certs', 'asset', assetId] as const,
  company: ['certs', 'company'] as const,
  rules: ['certs', 'rules'] as const,
  kinds: ['certs', 'kinds'] as const,
};

function fail(error: { message: string; code?: string } | null) {
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
}

/** One asset's rows: each kind its category requires, and each kind it holds a certificate for. */
export function useAssetCertStatus(assetId: string) {
  return useQuery({
    queryKey: certKeys.asset(assetId),
    enabled: !!assetId,
    queryFn: async () => {
      const { data, error } = await supabase()
        .from('asset_cert_status')
        .select('*')
        .eq('asset_id', assetId);
      fail(error);
      return (data ?? []) as CertStatusRow[];
    },
  });
}

/** Every visible asset's certificate rows (Assets list filter, Compliance report), in pages of 1000. */
export function useCompanyCertStatus(enabled = true) {
  return useQuery({
    queryKey: certKeys.company,
    enabled,
    queryFn: async () => {
      const rows: CertStatusRow[] = [];
      for (let from = 0; ; from += 1000) {
        const { data, error } = await supabase()
          .from('asset_cert_status')
          .select('*')
          .order('asset_id')
          .order('kind')
          .range(from, from + 999);
        fail(error);
        rows.push(...((data ?? []) as CertStatusRow[]));
        if ((data ?? []).length < 1000) break;
      }
      return rows;
    },
  });
}

export function useCertRules() {
  return useQuery({
    queryKey: certKeys.rules,
    queryFn: async () => {
      const { data, error } = await supabase()
        .from('category_cert_rules')
        .select('id, category_id, kind, valid_for_days, blocks_dispatch, remind_days_before')
        .order('kind');
      fail(error);
      return (data ?? []) as CertRule[];
    },
  });
}

/** Kinds already in use in the account (rules and certificates) — the kind field's suggestions. */
export function useCertKinds() {
  return useQuery({
    queryKey: certKeys.kinds,
    queryFn: async () => {
      const [rules, certs] = await Promise.all([
        supabase().from('category_cert_rules').select('kind').limit(500),
        supabase().from('asset_certificates').select('kind').limit(2000),
      ]);
      fail(rules.error);
      fail(certs.error);
      return kindSuggestions([
        ...((rules.data ?? []) as { kind: string }[]).map((r) => r.kind),
        ...((certs.data ?? []) as { kind: string }[]).map((r) => r.kind),
      ]);
    },
    staleTime: 5 * 60_000,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return (assetId?: string) => {
    void queryClient.invalidateQueries({ queryKey: certKeys.all });
    void queryClient.invalidateQueries({ queryKey: ['needs-action'] });
    if (assetId) void queryClient.invalidateQueries({ queryKey: lifecycleKeys.asset(assetId) });
  };
}

export interface CertificateInput {
  id?: string;
  kind: string;
  number: string;
  issuer: string;
  issued_on: string;
  expires_on: string;
  note: string;
  /** a new file to attach (replaces the current one) */
  file?: File | null;
  /** the current file, kept when no new one is chosen */
  document_path?: string | null;
}

/** Add or edit a certificate: upload the file first, write the row, and remove an orphaned or replaced file. */
export function useSaveCertificate(companyId: string, assetId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (c: CertificateInput) => {
      let path = c.document_path ?? null;
      let uploaded: string | null = null;
      if (c.file) {
        uploaded = docPath(companyId, 'certs', crypto.randomUUID(), c.file.type);
        const up = await supabase()
          .storage.from(DOC_BUCKET)
          .upload(uploaded, c.file, { contentType: c.file.type, upsert: false });
        if (up.error) throw new Error(up.error.message);
        path = uploaded;
      }
      const row = {
        kind: c.kind.trim(),
        number: c.number.trim() || null,
        issuer: c.issuer.trim() || null,
        issued_on: c.issued_on || null,
        expires_on: c.expires_on,
        note: c.note.trim() || null,
        document_path: path,
      };
      const { error } = c.id
        ? await supabase().from('asset_certificates').update(row).eq('id', c.id)
        : await supabase()
            .from('asset_certificates')
            .insert({ ...row, company_id: companyId, asset_id: assetId });
      if (error) {
        if (uploaded) await supabase().storage.from(DOC_BUCKET).remove([uploaded]);
        throw Object.assign(new Error(error.message), { code: error.code });
      }
      // the replaced file goes (best effort)
      if (uploaded && c.document_path)
        await supabase().storage.from(DOC_BUCKET).remove([c.document_path]);
    },
    onSettled: () => invalidate(assetId),
  });
}

export function useDeleteCertificate(assetId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (c: { id: string; document_path: string | null }) => {
      const { error } = await supabase().from('asset_certificates').delete().eq('id', c.id);
      fail(error);
      if (c.document_path) await supabase().storage.from(DOC_BUCKET).remove([c.document_path]);
    },
    onSettled: () => invalidate(assetId),
  });
}

/** Open a certificate / service document in a new tab (a signed URL valid 10 minutes). */
export async function openDocument(path: string) {
  // open the tab first (a popup blocker allows it inside the click), then point it at the file
  const tab = window.open('', '_blank');
  const { data, error } = await supabase().storage.from(DOC_BUCKET).createSignedUrl(path, 600);
  if (error || !data?.signedUrl) {
    tab?.close();
    throw new Error(error?.message ?? 'no url');
  }
  if (tab) tab.location.href = data.signedUrl;
  else window.location.href = data.signedUrl;
}

export interface RuleInput {
  id?: string;
  category_id: string;
  kind: string;
  valid_for_days: number | null;
  blocks_dispatch: boolean;
  remind_days_before: number;
}

export function useSaveCertRule(companyId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (r: RuleInput) => {
      const row = {
        kind: r.kind.trim(),
        valid_for_days: r.valid_for_days,
        blocks_dispatch: r.blocks_dispatch,
        remind_days_before: r.remind_days_before,
      };
      const { error } = r.id
        ? await supabase().from('category_cert_rules').update(row).eq('id', r.id)
        : await supabase()
            .from('category_cert_rules')
            .insert({ ...row, company_id: companyId, category_id: r.category_id });
      fail(error);
    },
    onSettled: () => invalidate(),
  });
}

export function useDeleteCertRule() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase().from('category_cert_rules').delete().eq('id', id);
      fail(error);
    },
    onSettled: () => invalidate(),
  });
}

export interface ComplianceRow extends CertStatusRow {
  asset_code: string;
  category_name: string | null;
  site_name: string | null;
}

/**
 * The Compliance report (§7.3): every LIVE asset whose category requires a certificate, one row per required kind,
 * worst first. The asset side comes from asset_overview (its cert_state is set exactly when something is required).
 */
export function useCompliance(enabled: boolean) {
  const certs = useCompanyCertStatus(enabled);
  const assets = useQuery({
    queryKey: [...certKeys.all, 'compliance-assets'],
    enabled,
    queryFn: async () => {
      const out: {
        asset_id: string;
        asset_code: string;
        category_name: string | null;
        custody_site_name: string | null;
      }[] = [];
      for (let from = 0; ; from += 1000) {
        const { data, error } = await supabase()
          .from('asset_overview')
          .select('asset_id, asset_code, category_name, custody_site_name')
          .eq('asset_status', 'active')
          .not('cert_state', 'is', null)
          .order('asset_code')
          .range(from, from + 999);
        fail(error);
        out.push(...((data ?? []) as typeof out));
        if ((data ?? []).length < 1000) break;
      }
      return out;
    },
  });
  const order = ['expired', 'missing', 'expiring', 'valid'];
  const byId = new Map((assets.data ?? []).map((a) => [a.asset_id, a]));
  const rows: ComplianceRow[] = (certs.data ?? [])
    .filter((r) => r.required && byId.has(r.asset_id))
    .map((r) => {
      const a = byId.get(r.asset_id)!;
      return {
        ...r,
        asset_code: a.asset_code,
        category_name: a.category_name,
        site_name: a.custody_site_name,
      };
    })
    .sort(
      (a, b) =>
        order.indexOf(a.state) - order.indexOf(b.state) ||
        (a.expires_on ?? '').localeCompare(b.expires_on ?? '') ||
        a.asset_code.localeCompare(b.asset_code),
    );
  return {
    isLoading: certs.isLoading || assets.isLoading,
    error: certs.error ?? assets.error,
    data: certs.data && assets.data ? rows : undefined,
  };
}
